// src/components/modals/ShareLinkModal.jsx
import React, { useEffect, useState } from "react";
import { Modal, Button, Form } from "react-bootstrap";
import axios from "axios";
import { toast } from "react-toastify";
import { QRCode } from "react-qrcode-logo";
import "./ShareLinkModal.css";
import "./ObjectExplorerModals.css";

// Helper: converte segundos de validade em ISO (null quando "Nunca expira")
function expiresToISO(seconds) {
  return seconds > 0
    ? new Date(Date.now() + seconds * 1000).toISOString()
    : null;
}

// Clipboard com fallback para evitar "Document is not focused"
async function copyToClipboard(text) {
  try {
    if (document.hasFocus() && navigator.clipboard && window.isSecureContext) {
      await navigator.clipboard.writeText(text);
    } else {
      const el = document.createElement("textarea");
      el.value = text;
      el.setAttribute("readonly", "");
      el.style.position = "fixed";
      el.style.left = "-9999px";
      document.body.appendChild(el);
      el.select();
      document.execCommand("copy");
      document.body.removeChild(el);
    }
    toast.success("Link copiado!");
  } catch {
    toast.error("Não foi possível copiar. Selecione e copie manualmente.");
  }
}

const ShareLinkModal = ({
  show,
  onHide,
  bucketName,
  objectKey,
  objectName,
  username,
  onShareSuccess,
}) => {
  const [url, setUrl] = useState("");
  const [expiresIn, setExpiresIn] = useState(0);
  const [loading, setLoading] = useState(false);
  const [showEmailInput, setShowEmailInput] = useState(false);
  const [recipientEmail, setRecipientEmail] = useState("");
  const [emailSending, setEmailSending] = useState(false);

  useEffect(() => {
    if (show) {
      setUrl("");
      setExpiresIn(0);
      setLoading(false);
      setShowEmailInput(false);
      setRecipientEmail("");
      setEmailSending(false);
    }
  }, [show]);

  const generateLink = async () => {
    if (!bucketName || !objectKey) {
      toast.error("Objeto inválido.");
      return;
    }

    setLoading(true);
    try {
      const jwt = localStorage.getItem("token");
      if (!jwt) {
        toast.error("Sessão expirada. Faça login novamente.");
        setLoading(false);
        return;
      }

      // backend já encurta quando short=1
      const baseUrl = `${import.meta.env.VITE_BACKEND_URL}/api/share/${encodeURIComponent(
        bucketName
      )}/${encodeURIComponent(objectKey)}`;

      const params = new URLSearchParams({
        expires: String(expiresIn), // 0 = público; >0 = privado (assinado Bunny)
        short: "1",
        ui: "1",
      });

      const { data } = await axios.get(`${baseUrl}?${params.toString()}`, {
        headers: { Authorization: `Bearer ${jwt}` },
      });

      const finalUrl = data?.url;
      if (!finalUrl) throw new Error("Resposta sem URL.");
      setUrl(finalUrl);

      if (typeof onShareSuccess === "function" && data?.public === true) {
        onShareSuccess({ bucket: bucketName, key: objectKey, public: true });
      }
    } catch (err) {
      console.error("Erro ao gerar link:", err);
      const msg =
        err?.response?.data?.error ||
        err?.message ||
        "Falha ao gerar o link";
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  const handleCopy = async () => {
    if (!url) return;
    await copyToClipboard(url);
  };

  const handleShareWhatsApp = () => {
    if (!url) return;
    const msg = `Confira este arquivo: ${url}`;
    window.open(`https://wa.me/?text=${encodeURIComponent(msg)}`, "_blank");
  };

  const handleSendEmail = async () => {
    if (!url) return toast.warning("Gere o link antes de enviar por e-mail.");

    // suporta múltiplos e-mails (vírgula, ; ou espaços)
    const to = recipientEmail
      .split(/[,\s;]+/)
      .map((s) => s.trim())
      .filter(Boolean);
    if (!to.length) return toast.warning("Informe ao menos um e-mail.");

    try {
      setEmailSending(true);
      const jwt = localStorage.getItem("token");
      if (!jwt) {
        toast.error("Sessão expirada. Faça login novamente.");
        setEmailSending(false);
        return;
      }

      await axios.post(
        `${import.meta.env.VITE_BACKEND_URL}/api/share/send-email`,
        {
          to,
          url, // curto ou longo
          fileName: objectName,
          objectKey,
          expiresAt: expiresToISO(expiresIn), // null quando "Nunca expira"
          senderName: username,
          senderEmail: localStorage.getItem("user_email") || undefined,
        },
        { headers: { Authorization: `Bearer ${jwt}` } }
      );

      toast.success("E-mail enviado com sucesso!");
      setShowEmailInput(false);
    } catch (err) {
      console.error("Erro ao enviar e-mail:", err);
      const msg = err?.response?.data?.error || "Falha ao enviar o e-mail";
      toast.error(msg);
    } finally {
      setEmailSending(false);
    }
  };

  return (
    <Modal show={show} onHide={onHide} centered className="share-modal object-modal">
      <Modal.Header closeButton>
        <Modal.Title className="modal-share-title">
          <div className="d-flex flex-column">
            <span className="label">Compartilhar:</span>
            <span className="filename-wrap object-modal-subtitle">{objectName}</span>
          </div>
        </Modal.Title>
      </Modal.Header>

      <Modal.Body>
        <Form.Group className="mb-3 text-start">
          <Form.Label>Validade do link</Form.Label>
          <Form.Select
            value={expiresIn}
            onChange={(e) => setExpiresIn(Number(e.target.value))}
            disabled={loading}
          >
            <option value={0}>Nunca expira</option>
            <option value={3600}>1 hora</option>
            <option value={86400}>1 dia</option>
            <option value={604800}>7 dias</option>
          </Form.Select>
        </Form.Group>

        {!url && (
          <Button className="w-100 mb-3" onClick={generateLink} disabled={loading}>
            {loading ? "Gerando..." : "Gerar Link"}
          </Button>
        )}

        {url && (
          <>
            <Form.Group className="mb-3 text-start">
              <Form.Label>Link Gerado</Form.Label>
              <Form.Control type="text" readOnly value={url} />
            </Form.Group>

            <div className="share-buttons-row mb-3 object-modal-actions-inline">
              <Button variant="primary" onClick={handleCopy}>
                Copiar
              </Button>
              <Button className="btn-whatsapp" onClick={handleShareWhatsApp}>
                <img
                  src="/whatsapp-icon.png"
                  alt="WhatsApp"
                  style={{ width: 20, height: 20 }}
                />{" "}
                WhatsApp
              </Button>
              <Button
                variant="outline-secondary"
                onClick={() => setShowEmailInput(true)}
              >
                📧 E-mail
              </Button>
            </div>

            {showEmailInput && (
              <div className="mb-3">
                <Form.Control
                  type="text"
                  placeholder="E-mails (separe por vírgula ou ; )"
                  value={recipientEmail}
                  onChange={(e) => setRecipientEmail(e.target.value)}
                  className="mb-2"
                />
                <Button
                  className="w-100 mb-3"
                  onClick={handleSendEmail}
                  disabled={emailSending}
                >
                  {emailSending ? "Enviando..." : "Enviar E-mail"}
                </Button>
              </div>
            )}

            <div className="qr-container text-center">
              <QRCode
                value={url}
                size={140}
                quietZone={12}
                logoImage="/VD-fav4.png"
                logoWidth={40}
                logoHeight={40}
                removeQrCodeBehindLogo
                ecLevel="H"
              />
              <small className="text-muted d-block mt-2">
                Escaneie o QR Code para acessar o link
              </small>
            </div>
          </>
        )}
      </Modal.Body>

      <Modal.Footer>
        <Button variant="secondary" onClick={onHide}>
          Fechar
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default ShareLinkModal;
